import { useEffect, useState } from "react";
import { Plus, Send, Eye, Trash2, X, Clock, Users } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const BLANK = { name: "", channel: "email", subject: "", body: "", offer_text: "", coupon_code: "", link: "", audience: { type: "all_eligible", tags: [], customer_ids: [] }, scheduled_at: "" };

export default function AdminCampaigns() {
  const [list, setList] = useState(null);
  const [templates, setTemplates] = useState({});
  const [tags, setTags] = useState([]);
  const [f, setF] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/campaigns").then(({ data }) => setList(data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => {
    load();
    api.get("/admin/campaigns/templates").then(({ data }) => setTemplates(data));
    api.get("/admin/crm/tags").then(({ data }) => setTags(data.tags));
  }, []);

  const applyTemplate = (key) => {
    const t = templates[key];
    if (t) setF({ ...f, subject: t.subject, body: t.body });
  };

  const create = async () => {
    if (!f.name.trim() || !f.body.trim()) { toast.error("Campaign name and message are required"); return null; }
    try {
      const { data } = await api.post("/admin/campaigns", { ...f, scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : "" });
      toast.success("Campaign created");
      setF(BLANK); setShowForm(false); load();
      return data;
    } catch (e) { toast.error(e.friendlyMessage); return null; }
  };

  const createAnd = async (then) => {
    if (busy) return;
    setBusy(then);
    const c = await create();
    if (c) {
      if (then === "send") await send(c.id);
      if (then === "schedule") await schedule(c.id, f.scheduled_at);
    }
    setBusy("");
  };

  const send = async (id) => {
    try {
      await api.post(`/admin/campaigns/${id}/send`);
      toast.success("Campaign sending started — watch the stats update");
      setTimeout(load, 4000);
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const schedule = async (id, when) => {
    if (!when) { toast.error("Pick a schedule date/time first"); return; }
    try {
      await api.put(`/admin/campaigns/${id}`, { scheduled_at: new Date(when).toISOString(), status: "scheduled" });
      toast.success("Campaign scheduled — the scheduler sends it automatically at the set time");
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const doPreview = async (id) => {
    try {
      const { data } = await api.post(`/admin/campaigns/${id}/preview`);
      setPreview(data);
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const del = async (id) => { if (!window.confirm("Delete this campaign?")) return; await api.delete(`/admin/campaigns/${id}`); toast.success("Deleted"); load(); };

  const statusStyle = { draft: "bg-[#F4EFE6] text-[#8C7A5E]", scheduled: "bg-[#D4AF37]/15 text-[#8C7A5E]", sending: "bg-blue-50 text-blue-700", sent: "bg-[#1A3C34]/10 text-[#1A3C34]", sent_with_errors: "bg-[#D4AF37]/20 text-[#8C7A5E]", failed: "bg-[#8B1E21]/10 text-[#8B1E21]" };

  return (
    <div className="max-w-5xl" data-testid="admin-campaigns">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl">Marketing Campaigns</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Email, SMS & WhatsApp campaigns to opted-in customers only. Consent is enforced automatically at send time.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="campaign-new-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Campaign</button>
      </div>

      {showForm && (
        <div className="bg-white border border-[#C5A059]/50 p-6 mb-6" data-testid="campaign-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="tq-label !text-[10px]">Campaign Name *</label>
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="campaign-name" className="tq-input !py-2 !text-sm" placeholder="Diwali Festive Offer" /></div>
            <div><label className="tq-label !text-[10px]">Channel</label>
              <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="campaign-channel" className="tq-input !py-2 !text-sm bg-white">
                <option value="email">Email</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp (approved template)</option>
              </select></div>
            <div><label className="tq-label !text-[10px]">Start from template</label>
              <select onChange={(e) => applyTemplate(e.target.value)} data-testid="campaign-template" className="tq-input !py-2 !text-sm bg-white" defaultValue="">
                <option value="">Choose…</option>
                {Object.entries(templates).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
              </select></div>
            <div><label className="tq-label !text-[10px]">Audience</label>
              <select value={f.audience.type} onChange={(e) => setF({ ...f, audience: { ...f.audience, type: e.target.value } })} data-testid="campaign-audience" className="tq-input !py-2 !text-sm bg-white">
                <option value="all_eligible">All eligible (consented) customers</option>
                <option value="tags">Customer groups / tags</option>
              </select></div>
            {f.audience.type === "tags" && (
              <div className="sm:col-span-2"><label className="tq-label !text-[10px]">Groups</label>
                <div className="flex flex-wrap gap-2">
                  {tags.length === 0 && <p className="text-xs text-[#9a938a]">No tags yet — tag customers in Customer Database first.</p>}
                  {tags.map((t) => (
                    <label key={t} className="flex items-center gap-1.5 text-xs border border-[#E5DEC9] px-2.5 py-1.5 cursor-pointer">
                      <input type="checkbox" checked={f.audience.tags.includes(t)} data-testid={`campaign-tag-${t}`}
                        onChange={(e) => setF({ ...f, audience: { ...f.audience, tags: e.target.checked ? [...f.audience.tags, t] : f.audience.tags.filter((x) => x !== t) } })}
                        className="accent-[#D4AF37] w-3.5 h-3.5" />{t}
                    </label>
                  ))}
                </div></div>
            )}
            {f.channel === "email" && (
              <div className="sm:col-span-2"><label className="tq-label !text-[10px]">Email Subject</label>
                <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="campaign-subject" className="tq-input !py-2 !text-sm" /></div>
            )}
            <div className="sm:col-span-2"><label className="tq-label !text-[10px]">{f.channel === "whatsapp" ? "Approved WhatsApp Template Name *" : "Message *"}</label>
              {f.channel === "whatsapp"
                ? <input value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="campaign-body" className="tq-input !py-2 !text-sm" placeholder="e.g. festive_offer (approved in Meta Business)" />
                : <textarea rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="campaign-body" className="tq-input !py-2 !text-sm" placeholder="Hi {name}, … {offer} {coupon} {link}" />}
              <p className="text-[10px] text-[#9a938a] mt-1">Placeholders: {"{name} {offer} {coupon} {link}"}</p></div>
            <div><label className="tq-label !text-[10px]">Offer / Discount Info</label>
              <input value={f.offer_text} onChange={(e) => setF({ ...f, offer_text: e.target.value })} data-testid="campaign-offer" className="tq-input !py-2 !text-sm" placeholder="Flat 20% off festive styles" /></div>
            <div><label className="tq-label !text-[10px]">Coupon Code</label>
              <input value={f.coupon_code} onChange={(e) => setF({ ...f, coupon_code: e.target.value.toUpperCase() })} data-testid="campaign-coupon" className="tq-input !py-2 !text-sm uppercase" /></div>
            <div><label className="tq-label !text-[10px]">Website / Product Link</label>
              <input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} data-testid="campaign-link" className="tq-input !py-2 !text-sm" placeholder="https://tarishqfashion.in/collections/sale" /></div>
            <div><label className="tq-label !text-[10px]">Schedule (optional)</label>
              <input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="campaign-schedule" className="tq-input !py-2 !text-sm" /></div>
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={() => createAnd("draft")} disabled={!!busy} data-testid="campaign-save-draft" className="tq-btn-outline !py-2.5">Save Draft</button>
            <button onClick={() => createAnd("send")} disabled={!!busy} data-testid="campaign-send-now" className="tq-btn-dark !py-2.5"><Send size={13} /> {busy === "send" ? "Sending…" : "Send Immediately"}</button>
            {f.scheduled_at && <button onClick={() => createAnd("schedule")} disabled={!!busy} data-testid="campaign-schedule-btn" className="tq-btn-outline !py-2.5"><Clock size={13} /> Schedule</button>}
          </div>
        </div>
      )}

      <h2 className="font-serif text-xl mb-3">Campaign History</h2>
      {!list ? <div className="h-40 bg-white animate-pulse" /> : list.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-10 text-center text-sm text-[#9a938a]" data-testid="campaigns-empty">No campaigns yet — create your first one above.</div>
      ) : (
        <div className="bg-white border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
          {list.map((c) => (
            <div key={c.id} className="p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`campaign-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-[11px] text-[#9a938a] flex items-center gap-2"><Users size={11} /> {c.stats?.total || 0} recipients · <span className="text-[#1A3C34]">{c.stats?.sent || 0} sent</span> · <span className="text-[#8B1E21]">{c.stats?.failed || 0} failed</span> · {c.stats?.skipped || 0} skipped (no consent)</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#8C7A5E]">{c.channel}</span>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${statusStyle[c.status] || ""}`}>{c.status?.replace(/_/g, " ")}{c.status === "scheduled" && c.scheduled_at ? ` · ${new Date(c.scheduled_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</span>
              <span className="text-xs text-[#9a938a]">{new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
              <div className="flex gap-1">
                <button onClick={() => doPreview(c.id)} aria-label="Preview" data-testid={`campaign-preview-${c.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><Eye size={13} /></button>
                {c.status === "draft" && <button onClick={() => send(c.id)} aria-label="Send" data-testid={`campaign-send-${c.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#1A3C34] text-[#1A3C34]"><Send size={13} /></button>}
                <button onClick={() => del(c.id)} aria-label="Delete" data-testid={`campaign-delete-${c.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#8B1E21] hover:text-[#8B1E21]"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="campaign-preview-modal">
          <div className="bg-white w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="font-serif text-xl">Campaign Preview</p>
              <button onClick={() => setPreview(null)} aria-label="Close"><X size={20} /></button>
            </div>
            <p className="text-xs text-[#1A3C34] font-semibold mb-3" data-testid="preview-recipients">{preview.recipients} eligible recipient(s) (consent verified)</p>
            {preview.subject && <p className="text-sm font-semibold mb-2">Subject: {preview.subject}</p>}
            <div className="bg-[#F4EFE6] p-4 text-sm whitespace-pre-wrap" data-testid="preview-body">{preview.body}</div>
            {preview.sample_to && <p className="text-[11px] text-[#9a938a] mt-2">Sample rendered for: {preview.sample_to}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
